import { useNavigate } from 'react-router-dom';
import background from '../../assets/background.svg';
import config from '../../config';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { apiProducts } from '../../api/apiProducts.api';
import { apiPayment } from '../../api/apiPayment.api';
import { axiosPrivate } from '../../axios';
import { openLink } from '../../utils/openLink';
import { useAcceptanceData } from '../../hooks/useAcceptanceData';
import { acceptanceType, resolverButtonText } from '../../hooks/useAcceptance';
import { useFullScreenModal } from '../../hooks/useFullScreenModal';
import DotSpinner from '../../components/shared/DotSpinner';
import { Button } from '../../components/Button';

const TITLE_SUBSCRIBE = {
  premium_weekly: 'Неделя',
  premium_monthly: 'Месяц',
  premium_quarterly: '3 месяца'
};

const CURRENCY_CONFIG = {
  BYN: {
    IconSmall: ({ className = '' }) => (
      <img src="/icons/BYN-small.svg" alt="currency" className={`w-[9px] ml-[4px] ${className}`} />
    )
  },
  STAR: {
    IconSmall: ({ className = '' }) => (
      <img src="/icons/tg_star.svg" alt="currency" className={`w-[14px] ml-[4px] ${className}`} />
    )
  }
};

const BENEFITS = [
  { icon: '/icons/paywall-card.svg', label: 'Лайки без ограничений' },
  { icon: '/icons/paywall-like.svg', label: 'Видите, кто вас лайкнул' },
  { icon: '/icons/paywall-superlike.svg', label: '5 супер-лайков в день' },
  { icon: '/icons/paywall-reload.svg', label: '5 возвратов в неделю' },
  { icon: '/icons/paywall-calendar.svg', label: 'Видите, кто идёт на мероприятие' },
  { icon: '/icons/paywall-gift.svg', label: 'Нет рекламы + подарок' }
];

// Дробная часть цены выводится верхним индексом: 7²² Б
function formatPrice(amount, currency) {
  if (currency === 'BYN') {
    const [int, frac] = Number(amount).toFixed(2).split('.');
    return { int: Number(int).toLocaleString('ru-RU'), frac };
  }
  return { int: Number(amount).toLocaleString('ru-RU'), frac: null };
}

function RadioCircle({ checked }) {
  return checked ? (
    <div className="w-[24px] h-[24px] flex items-center justify-center rounded-full border border-[#A1F69E] shrink-0">
      <div className="w-[12px] h-[12px] rounded-full bg-[#A1F69E]" />
    </div>
  ) : (
    <div className="w-[24px] h-[24px] rounded-full border border-[#A1F69E] shrink-0" />
  );
}

function SubscriptionRadioCard({ product, title, payment, isSelected, onClick }) {
  const priceInfo = product.prices.find((p) => p.currency === payment);
  if (!priceInfo) return null;

  const { int, frac } = formatPrice(priceInfo.amount, payment);
  const oldPrice = priceInfo.oldAmount;
  const discountPercent = oldPrice ? Math.round((1 - priceInfo.amount / oldPrice) * 100) : 0;
  const { IconSmall } = CURRENCY_CONFIG[payment] || {};

  return (
    <div
      onClick={onClick}
      className={`flex flex-row justify-between items-center w-full rounded-[16px] border p-3 cursor-pointer transition-colors ${
        isSelected ? 'border-[#A1F69E] bg-[#043939]' : 'border-[#233636] bg-[#022424]'
      }`}
    >
      <div className="flex flex-row items-center gap-[12px]">
        <RadioCircle checked={isSelected} />
        <div className="flex flex-col">
          <div className="flex items-center gap-[8px]">
            <span className="font-raleway font-bold text-white text-[18px]">{title}</span>
          </div>
        </div>
      </div>

      <div className="flex items-baseline gap-[16px] font-raleway text-white font-semibold">
        {!!oldPrice && (
          <span class="relative text-white text-[22px] opacity-50 text-center">
            <div class="ml-[5px] flex flex-row items-center justify-center">
              {oldPrice.toLocaleString('ru-RU')} <IconSmall />
            </div>
            <span class="absolute inset-0 w-full h-[2px] bg-red-500 rotate-[12deg] top-1/2 left-0"></span>
          </span>
        )}
        <span className="flex items-center text-[22px] leading-none">
          {int}
          <IconSmall />
        </span>
      </div>
    </div>
  );
}

export function Paywall() {
  const navigate = useNavigate();

  const [payment, setPayment] = useState('STAR');
  const [products, setProducts] = useState([]);
  const [productSelected, setProductSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAgree, setIsAgree] = useState(false);

  const { acceptance, onAccept } = useAcceptanceData(acceptanceType.offer_for_the_provision_of_paid_services);
  const { Modal, setOpen } = useFullScreenModal();

  useEffect(() => {
    setIsLoading(true);
    apiProducts
      .fetchAllProducts(axiosPrivate, { type: 'subscription' })
      .then((res) => {
        // Сортируем по длительности (дни): Неделя -> Месяц -> 3 месяца
        const items = [...(res.data.items || [])].sort((a, b) => a.baseAmount - b.baseAmount);
        setProducts(items);
        setProductSelected(items[1] || items[0] || null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleByProduct = async () => {
    if (config.PURCHASES_FROZEN) {
      toast.info(config.PURCHASES_FROZEN_MESSAGE);
      return;
    }
    if (!productSelected) return;

    try {
      await onAccept();

      const response =
        payment === 'BYN'
          ? await apiPayment.createOrder(axiosPrivate, productSelected._id)
          : await apiPayment.createInvoice(axiosPrivate, productSelected._id);

      if (payment === 'BYN') {
        openLink(response.redirectUrl);
      } else {
        toast.success('Оплатите заказ в боте!');
      }

      // Покупка оформлена — отправляем на страницу знакомств
      navigate(config.ROUTES.MEET);
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }


  return (
    <div className="relative w-[100%] h-[100%] overflow-x-hidden overflow-y-auto px-4 pt-[21px] pb-[180px]">
      <img src={background} alt="" className="absolute inset-0 w-full pointer-events-none" />

      <div className="relative z-1 flex flex-col">
        <button
          type="button"
          onClick={() => navigate(config.ROUTES.MEET)}
          className="ml-auto mb-[20px] block cursor-pointer"
        >
          <img alt="close" src="/icons/Button-close.svg" className="w-[44px] h-[44px]" />
        </button>

        <h1 className="text-[32px] font-bold text-[#A1F69E] font-raleway text-center">Mint premium</h1>

        <p className="text-[16px] text-white text-center font-raleway font-bold mb-[11px]">
          Вы найдёте свою любовь в разы быстрее
        </p>

        {/* Переключение валюты: у BYN и звёзд свои расценки */}
        <div className="w-full flex items-center justify-between bg-[#022424] p-[2px] rounded-[400px] font-raleway border border-[#233636] mb-[20px]">
          <div
            onClick={() =>
              config.BYN_PAYMENT_FROZEN ? toast.info(config.PURCHASES_FROZEN_MESSAGE) : setPayment('BYN')
            }
            className={` ${payment === 'BYN' ? 'bg-[#043939]' : 'bg-transparent'} w-1/2 h-[33px] rounded-[400px] flex justify-center items-center `}
          >
            <span className={`${payment === 'BYN' ? 'text-[#A1F69E]' : 'text-white'} text-[14px]`}>Деньги</span>
          </div>
          <div
            onClick={() => setPayment('STAR')}
            className={` ${payment === 'STAR' ? 'bg-[#043939]' : 'bg-transparent'} w-1/2 h-[33px] rounded-[400px] flex justify-center items-center`}
          >
            <span className={`${payment === 'STAR' ? 'text-[#A1F69E]' : 'text-white'} text-[14px]`}>
              Telegram звезды
            </span>
          </div>
        </div>

        {/* Тарифы: Неделя / Месяц / 3 месяца */}
        <div className="flex flex-col gap-[12px] mb-[20px]">
          {products.map((p) => (
            <SubscriptionRadioCard
              key={p._id}
              product={p}
              title={TITLE_SUBSCRIBE[p.slug] || p.name}
              payment={payment}
              isSelected={productSelected?._id === p._id}
              onClick={() => setProductSelected(p)}
            />
          ))}
        </div>

        {/* Преимущества подписки */}
        <h2 className="font-raleway font-bold text-white text-[16px] mb-[12px]">Вы получаете с подпиской:</h2>
        <div className="grid grid-cols-2 gap-[12px] mb-[20px]">
          {BENEFITS.map((benefit) => (
            <div
              key={benefit.label}
              className="flex items-center gap-[10px] bg-[#010D0D] border border-[#022424] rounded-[12px] px-[12px] py-[14px]"
            >
              <img src={benefit.icon} alt="" className="w-[28px] h-[28px] shrink-0" />
              <span className="font-raleway text-white text-[13px] font-semibold leading-tight">
                {benefit.label}
              </span>
            </div>
          ))}
        </div>

        <p className="font-raleway text-[12px] text-white opacity-70 text-justify">
          Подписка будет продлеваться автоматически, если она не отменена как минимум за 24 часа до окончания текущего
          периода. Оплата спишется автоматически после окончания пробного периода, если он был оформлен. Отменить
          подписку можно в любой момент в настройках профиля
        </p>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-10 px-4 pb-[24px] pt-[16px] bg-gradient-to-t from-[#021A1A] via-[#021A1A] to-transparent">
        <Button onClick={handleByProduct} className="w-full mb-[10px]" disabled={!isAgree || isLoading || !productSelected}>
          Продолжить
        </Button>

        <div className="flex justify-start items-start gap-[12px] mb-[12px]">
          {isAgree ? (
            <img
              className="w-[24px] cursor-pointer shrink-0"
              src="/icons/famicons_checkbox.svg"
              onClick={() => setIsAgree(false)}
            />
          ) : (
            <div
              className="w-[24px] h-[24px] cursor-pointer border border-white rounded-[4px] shrink-0"
              onClick={() => setIsAgree(true)}
            />
          )}

          <p className="font-raleway font-medium text-[13px] text-white text-justify">
            Активируя подписку, вы соглашаетесь с{' '}
            <span onClick={() => setOpen(true)} className="underline text-[#A1F69E] cursor-pointer pointer-events-auto">
              {resolverButtonText[acceptanceType.offer_for_the_provision_of_paid_services]}
            </span>
          </p>
        </div>
      </div>

      {acceptance && (
        <Modal title={acceptance.title}>
          <div className="whitespace-pre-line">{acceptance.content}</div>
        </Modal>
      )}
    </div>
  );
}
